/* eslint-disable @typescript-eslint/naming-convention */
import classNames from 'classnames';
import flat from 'flat';
import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { mergeObjects } from '../../../../common/src/utils/mergeObjects';
import { getInspectionStandardTypes } from '../../customerSettings';
import { customInspectionTypeFrontEndSettings } from '../../customInspectionTypes/customInspectionTypeFrontEndSettings';
import { IWithReactRouterProps } from '../../mixins/router/withReactRouter';
import { ITranslateProps, translate } from '../../mixins/translate';
import { IValidationState } from '../../mixins/validation';
import { fetchDelete, fetchGet, fetchJsonWithThrow, fetchPost } from '../../store/fetch';
import { elementConstructionTypeList } from '../../utils/data/elementConstructionTypeList';
import { materialList } from '../../utils/data/materialList';
import { InspectionStandardTypes } from '../../utils/enums/InspectionStandardTypes';
import { Roles } from '../../utils/enums/Roles';
import { IConnectProps } from '../../utils/IConnectProps';
import { isCustomerVersion } from '../../utils/isCustomerVersion';
import { isEemuaOrApi } from '../../utils/isEemuaOrApi';
import {
    Content,
    EditPanelButton,
    EditPanelButtons,
    EditPanelCell,
    EditPanelInput,
    EditPanelRow,
    EditPanelSection,
    EditPanelSelect,
    EditPanelSwitch,
    Panel,
    PanelBody,
    PanelButtonLink,
    PanelHeading,
    PanelRouterLink,
    PanelWithoutForm,
    Table,
    TableBody,
    TableHead,
    TableHeader,
    TableHeadRow,
    TableRow,
    Topbar,
    TopbarCrumb,
    TopbarCrumbActive,
    TopbarLeft,
    TopbarRight,
    Tray,
} from '../elements';
import { IElement } from '../interfaces/IElement';
import { IManifestation } from '../interfaces/IManifestation';
import { IUnit } from '../interfaces/IUnit';
import { Component } from '../utils/Component';
import RoleDependent from '../utils/RoleDependent';
import { ElementRow } from './ElementRow';
import { ObjectCompositionCopy } from './ObjectCompositionCopy';

interface IObjectCompositionEditProps extends ITranslateProps, IConnectProps, IWithReactRouterProps {
    showHeader?: boolean;
}

interface ICategory {
    id: string;
    name: string;
}

interface IObjectCompositionEditState extends IValidationState {
    elements: IElement[];
    categories: ICategory[];
    surveyId?: undefined | string;
    name?: undefined | string;
    compositionChanged: boolean;
    inspectionStandardId?: undefined | string;
    inspectionStandardType?: undefined | string;
    canEditObject: boolean;

    showElementForm: boolean;
    editElementId?: undefined | string;
    showUnitForm: boolean;
    editUnitId?: undefined | string;
    objectTypeElements: { code: string; name: string; units: any[] }[];
    objectTypeUnits: { code: string; name: string; manifestations: any[] }[];
    showManifestationEditForm: boolean;
    showManifestationAddForm: boolean;

    addElementNameRequiredError?: undefined | boolean;
    elementFormObjectTypeElementCode?: undefined | string;
    elementFormName?: undefined | string;
    elementFormCode?: undefined | string;
    elementFormLocation?: undefined | string;
    elementFormId?: undefined | string;
    elementConstructionYear?: undefined | number | null;
    elementConstructionType?: undefined | string;
    elementIsStructural: boolean;
    elementGroupName?: undefined | string;
    elementIsArchived?: undefined | boolean;
    elementIsSurveySpecific?: undefined | boolean;
    elementInspectionStandardData: any;
    elementCategoryId?: undefined | string;
    elementSupplier: string;
    elementRemarks: string;

    addUnitNameRequiredError?: undefined | boolean;
    unitFormObjectTypeUnitCode?: undefined | string;
    unitFormName?: undefined | string;
    unitFormCode?: undefined | string;
    unitFormMaterial?: undefined | string;
    unitFormLocation?: undefined | string;
    unitFormId?: undefined | string;
    unitFormElementId?: undefined | string;
    unitFormQuantity?: undefined | number;
    unitFormQuantityUnitOfMeasurement?: undefined | string;
    unitConstructionYear?: undefined | number | null;
    unitIsArchived?: undefined | boolean;
    unitIsSurveySpecific?: undefined | boolean;
    unitInspectionStandardData: any;
    unitSupplier?: string | undefined;
    unitRemarks?: string | undefined;
    unitDimension?: string | undefined;
    unitStrength?: string | undefined;
    unitNorm?: string | undefined;

    addManifestationNameRequiredError?: undefined | boolean;
    manifestationFormName?: undefined | string;
    manifestationFormCode?: undefined | string;
    manifestationFormLocation?: undefined | string;
    manifestationFormId?: undefined | string;
    manifestationFormElementId?: undefined | string;
    manifestationFormUnitId?: undefined | string;
    manifestationFormTotal?: undefined | number;
    manifestationIsSurveySpecific?: undefined | boolean;

    copyDestinationObjects?: undefined | { id: string; name: string }[];
}

function mapStateToProps() {
    return {};
}

class ObjectCompositionEdit extends Component<IObjectCompositionEditProps, IObjectCompositionEditState> {
    constructor(props: IObjectCompositionEditProps) {
        super(props);

        this.state = {
            submitting: false,
            failed: false,
            saved: false,
            canEditObject: false,
            surveyId: this.props.location.query.surveyid,
            compositionChanged: false,
            elements: [],
            categories: [],
            objectTypeElements: [],
            objectTypeUnits: [],
            showElementForm: false,
            showUnitForm: false,
            showManifestationEditForm: false,
            showManifestationAddForm: false,
            elementIsStructural: false,
            elementInspectionStandardData: {},
            unitInspectionStandardData: {},
            elementSupplier: '',
            elementRemarks: '',
            unitSupplier: '',
            unitRemarks: '',
            unitDimension: '',
            unitStrength: '',
            unitNorm: '',
        };
    }

    public override async componentDidMount() {
        await this.load();
        await this.loadObject();
        await this.loadCategories();
    }

    public override render() {
        return (
            <div>
                {this.props.showHeader && (
                    <Topbar>
                        <TopbarLeft>
                            <TopbarCrumbActive title={this.state.name} icon="fa-building" />
                            {!this.state.surveyId && (
                                <>
                                    <TopbarCrumb>
                                        <Link to={`/objects/${this.props.match.params.id}`}>{this.props.t('common.objectDetails')}</Link>
                                    </TopbarCrumb>
                                    <TopbarCrumb>
                                        <Link to="/objects">{this.props.t('common.objects')}</Link>
                                    </TopbarCrumb>
                                </>
                            )}
                        </TopbarLeft>
                        <TopbarRight>
                            {this.state.surveyId && (
                                <PanelRouterLink
                                    to={`/surveys/${this.props.location.query.surveyid}/${this.props.location.query.tab}`}
                                    text={this.props.t('survey.returnEditInspection')}
                                    icon="fa fa-angle-double-right"
                                    small
                                    primary
                                />
                            )}
                            {this.state.compositionChanged && this.props.location.query.surveyid && (
                                <PanelButtonLink
                                    mb0
                                    onClick={async () => this.resetDefaultMaintenance()}
                                    text={this.props.t('survey.resetDefaultMaintenance')}
                                    icon="fa fa-gears"
                                    small
                                    primary
                                />
                            )}
                            {this.props.location.query.objectadded && (
                                <PanelRouterLink
                                    to={`/surveys/add?objectId=${this.props.match.params.id}`}
                                    text={this.props.t('common.addItem', this.props.t('common.survey'))}
                                    icon="fa fa-plus"
                                    small
                                    primary
                                />
                            )}
                        </TopbarRight>
                    </Topbar>
                )}

                <Content>
                    <Tray>
                        {!this.state.surveyId && this.props.showHeader && (
                            <div className="panel-heading mw1000 center-block">
                                <ul className="nav panel-tabs-border panel-tabs-merge panel-tabs">
                                    <li>
                                        <Link to={`/objects/${this.props.match.params.id}/edit`} aria-expanded="true">
                                            {this.props.t('common.object')}
                                        </Link>
                                    </li>
                                    <li className="active">
                                        <a href="#" aria-expanded="true">
                                            {this.props.t('common.composition')}
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        )}
                        <Panel>
                            <PanelBody>
                                <form method="post" action="/" id="admin-form">
                                    <Table>
                                        {this.state.submitting && (
                                            <TableBody>
                                                <TableRow>
                                                    <td colSpan={4} className="text-center">
                                                        <img src="/assets/img/spinner.svg" alt=" loading" />
                                                    </td>
                                                </TableRow>
                                            </TableBody>
                                        )}
                                        {isEemuaOrApi(this.state.inspectionStandardType) &&
                                            this.state.categories.length > 0 &&
                                            this.state.elements.length > 0 &&
                                            this.state.categories.map((category, i) => [
                                                <React.Fragment key={i}>
                                                    <TableHead>
                                                        <tr className="active mt10">
                                                            <th colSpan={4} className="p10 fw600 fs16">
                                                                {category.name}{' '}
                                                            </th>
                                                        </tr>
                                                        <tr className="dark">
                                                            <TableHeader>{this.props.t('common.code')}</TableHeader>
                                                            <TableHeader>{this.props.t('common.part')}</TableHeader>
                                                            <TableHeader>{this.props.t('object.inspectionSpecific')}</TableHeader>
                                                            <TableHeader>&nbsp; </TableHeader>
                                                            <TableHeader>&nbsp; </TableHeader>
                                                            <TableHeader>&nbsp; </TableHeader>
                                                            <TableHeader>&nbsp; </TableHeader>
                                                        </tr>
                                                    </TableHead>
                                                    {this.state.elements
                                                        .filter((element: any) => element.categoryId === category.id)
                                                        .map((element, j) => [
                                                            <ElementRow
                                                                key={j}
                                                                element={element}
                                                                submitting={this.state.submitting}
                                                                onRemoveElement={() => this.removeElement(element)}
                                                                onRemoveUnit={async (unit) => this.removeUnit(unit)}
                                                                onRemoveManifestation={(manifestation) => this.removeManifestation(manifestation)}
                                                                onEditElement={(elementId) => this.handleEditElementClicked(elementId)}
                                                                onAddUnit={(elementId, elementCode) =>
                                                                    this.handleAddUnitClicked(elementId, elementCode)
                                                                }
                                                                onEditUnit={(elementId, unitId) => this.handleEditUnitClicked(elementId, unitId)}
                                                                onAddManifestations={(unit) => this.handleAddManifestations(unit)}
                                                                onEditManifestation={(manifestation) =>
                                                                    this.handleEditManifestationClicked(manifestation)
                                                                }
                                                                showAddManifestations={this.useManifestations()}
                                                            />,
                                                        ])}
                                                    <TableBody>
                                                        <TableRow>
                                                            <td colSpan={7} align="right">
                                                                <EditPanelButton
                                                                    onClick={() => this.handleAddElementClicked(category.id)}
                                                                    text={this.props.t('common.addItem', this.props.t('common.element'))}
                                                                    iconClass="fa-plus"
                                                                    small
                                                                    primary
                                                                />
                                                            </td>
                                                        </TableRow>
                                                    </TableBody>
                                                </React.Fragment>,
                                            ])}

                                        {!isEemuaOrApi(this.state.inspectionStandardType) && (
                                            <>
                                                <TableHead>
                                                    <TableHeadRow>
                                                        <TableHeader>{this.props.t('common.code')}</TableHeader>
                                                        <TableHeader>{this.props.t('common.part')}</TableHeader>
                                                        <TableHeader>{this.props.t('object.inspectionSpecific')}</TableHeader>
                                                        <TableHeader>{this.props.t('common.material')}</TableHeader>
                                                        <TableHeader>{this.props.t('common.quantity')}</TableHeader>
                                                        <TableHeader>{this.props.t('common.year')}</TableHeader>
                                                        <TableHeader>&nbsp; </TableHeader>
                                                    </TableHeadRow>
                                                </TableHead>
                                                {this.state.elements.map((element, i) => [
                                                    <ElementRow
                                                        key={i}
                                                        element={element}
                                                        submitting={this.state.submitting}
                                                        onRemoveElement={() => this.removeElement(element)}
                                                        onRemoveUnit={async (unit) => this.removeUnit(unit)}
                                                        onRemoveManifestation={(manifestation) => this.removeManifestation(manifestation)}
                                                        onEditElement={(elementId) => this.handleEditElementClicked(elementId)}
                                                        onAddUnit={(elementId, elementCode) => this.handleAddUnitClicked(elementId, elementCode)}
                                                        onEditUnit={(elementId, unitId) => this.handleEditUnitClicked(elementId, unitId)}
                                                        onAddManifestations={(unit) => this.handleAddManifestations(unit)}
                                                        onEditManifestation={(manifestation) => this.handleEditManifestationClicked(manifestation)}
                                                        showAddManifestations={this.useManifestations()}
                                                    />,
                                                ])}
                                            </>
                                        )}
                                    </Table>
                                </form>
                            </PanelBody>
                            <EditPanelButtons footer>
                                {!isEemuaOrApi(this.state.inspectionStandardType) && (
                                    <>
                                        <ObjectCompositionCopy objectId={this.props.match.params.id} onCopied={async () => this.load()} />
                                        <EditPanelButton
                                            onClick={() => this.handleAddElementClicked()}
                                            text={this.props.t('common.addItem', this.props.t('common.element'))}
                                            iconClass="fa-plus"
                                            small
                                            primary
                                        />
                                    </>
                                )}
                            </EditPanelButtons>
                            {/* Add/edit unit */}
                            <div id="popup_unit" className={classNames({ hidden: !this.state.showUnitForm })}>
                                <form method="post" action="/" id="admin-form">
                                    <PanelWithoutForm>
                                        <PanelHeading text={this.props.t('common.unit')} />
                                        <div className="panel-body">
                                            {this.state.objectTypeUnits.length > 0 && (
                                                <EditPanelSection>
                                                    <label className="field select">
                                                        <select
                                                            id="company"
                                                            name="company"
                                                            value={this.state.unitFormObjectTypeUnitCode}
                                                            onChange={async (e) => this.handleChange(e.target.value, 'unitFormObjectTypeUnitCode')}
                                                        >
                                                            <option value="">{this.props.t('common.selectItem', this.props.t('common.unit'))}</option>
                                                            {this.state.objectTypeUnits.map((objectTypeUnit, i) => (
                                                                <option key={i} value={objectTypeUnit.code}>
                                                                    {objectTypeUnit.name} ({objectTypeUnit.code}){' '}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        <i className="arrow"></i>
                                                    </label>
                                                </EditPanelSection>
                                            )}
                                            <EditPanelSection>
                                                <EditPanelInput
                                                    name="unitFormName"
                                                    placeholder={this.props.t('object.customUnit')}
                                                    value={this.state.unitFormName}
                                                    icon="fa fa-pagelines"
                                                    handleChange={this.handleChange.bind(this)}
                                                    handleEnter={async () => this.handleSaveUnit()}
                                                    notValid={this.state.addUnitNameRequiredError}
                                                />
                                            </EditPanelSection>
                                            <EditPanelRow>
                                                <EditPanelCell rowCount={2}>
                                                    <EditPanelInput
                                                        name="unitFormCode"
                                                        placeholder={this.props.t('common.code')}
                                                        value={this.state.unitFormCode}
                                                        icon="fa fa-dashboard"
                                                        handleChange={this.handleChange.bind(this)}
                                                        handleEnter={async () => this.handleSaveUnit()}
                                                    />
                                                </EditPanelCell>
                                                <EditPanelCell rowCount={8}>
                                                    <EditPanelInput
                                                        name="unitFormLocation"
                                                        placeholder={this.props.t('common.location')}
                                                        value={this.state.unitFormLocation}
                                                        icon="fa fa-map-marker"
                                                        handleChange={this.handleChange.bind(this)}
                                                        handleEnter={async () => this.handleSaveUnit()}
                                                    />
                                                </EditPanelCell>
                                                <EditPanelCell rowCount={2}>
                                                    <EditPanelInput
                                                        name="unitConstructionYear"
                                                        placeholder={this.props.t('object.constructionYear')}
                                                        value={this.state.unitConstructionYear}
                                                        restrictToNumbers={true}
                                                        handleChange={(value) =>
                                                            void this.handleChange(value === '' ? null : parseInt(value, 10), 'unitConstructionYear')
                                                        }
                                                        handleEnter={async () => this.handleSaveUnit()}
                                                    />
                                                </EditPanelCell>
                                            </EditPanelRow>
                                            <EditPanelRow>
                                                {/* Material */}
                                                <EditPanelCell rowCount={2}>
                                                    <EditPanelInput
                                                        name="unitFormQuantity"
                                                        placeholder={this.props.t('common.quantity')}
                                                        value={this.state.unitFormQuantity}
                                                        restrictToNumbers={true}
                                                        handleChange={this.handleChange.bind(this)}
                                                        handleEnter={async () => this.handleSaveUnit()}
                                                    />
                                                </EditPanelCell>
                                                <EditPanelCell rowCount={2}>
                                                    <EditPanelSelect
                                                        name="unitFormQuantityUnitOfMeasurement"
                                                        value={this.state.unitFormQuantityUnitOfMeasurement}
                                                        handleChange={this.handleChange.bind(this)}
                                                        options={[
                                                            { value: 'm', text: 'm' },
                                                            { value: 'm2', text: 'm2' },
                                                            { value: 'm3', text: 'm3' },
                                                            { value: 'pcs', text: this.props.t('common.pcs') },
                                                        ]}
                                                    />
                                                </EditPanelCell>
                                                <EditPanelCell rowCount={4}>
                                                    <EditPanelInput
                                                        name="unitDimension"
                                                        placeholder={this.props.t('object.dimension')}
                                                        value={this.state.unitDimension}
                                                        handleChange={this.handleChange.bind(this)}
                                                        handleEnter={async () => this.handleSaveUnit()}
                                                    />
                                                </EditPanelCell>
                                                <EditPanelCell rowCount={4}>
                                                    {(!isCustomerVersion('ronik') && (
                                                        <EditPanelSelect
                                                            name="unitFormMaterial"
                                                            value={this.state.unitFormMaterial}
                                                            handleChange={this.handleChange.bind(this)}
                                                            placeholder={this.props.t('common.material')}
                                                            options={materialList()}
                                                        />
                                                    )) || (
                                                        <EditPanelInput
                                                            name="unitFormMaterial"
                                                            placeholder={this.props.t('common.material')}
                                                            value={this.state.unitFormMaterial}
                                                            icon="fa fa-flask"
                                                            handleChange={this.handleChange.bind(this)}
                                                            handleEnter={async () => this.handleSaveUnit()}
                                                        />
                                                    )}
                                                </EditPanelCell>
                                            </EditPanelRow>
                                            <EditPanelRow>
                                                <EditPanelCell rowCount={4}>
                                                    <EditPanelInput
                                                        name="unitStrength"
                                                        placeholder={this.props.t('object.strength')}
                                                        value={this.state.unitStrength}
                                                        handleChange={this.handleChange.bind(this)}
                                                        handleEnter={async () => this.handleSaveUnit()}
                                                    />
                                                </EditPanelCell>
                                                <EditPanelCell rowCount={4}>
                                                    <EditPanelInput
                                                        name="unitNorm"
                                                        placeholder={this.props.t('object.norm')}
                                                        value={this.state.unitNorm}
                                                        handleChange={this.handleChange.bind(this)}
                                                        handleEnter={async () => this.handleSaveUnit()}
                                                    />
                                                </EditPanelCell>
                                                <EditPanelCell rowCount={4}>
                                                    <EditPanelInput
                                                        name="unitSupplier"
                                                        placeholder={this.props.t('object.supplier')}
                                                        value={this.state.unitSupplier}
                                                        handleChange={this.handleChange.bind(this)}
                                                        handleEnter={async () => this.handleSaveUnit()}
                                                    />
                                                </EditPanelCell>
                                            </EditPanelRow>
                                            <EditPanelRow>
                                                <EditPanelCell rowCount={12}>
                                                    <EditPanelInput
                                                        name="unitRemarks"
                                                        placeholder={this.props.t('common.remarks')}
                                                        value={this.state.unitRemarks}
                                                        handleChange={this.handleChange.bind(this)}
                                                        handleEnter={async () => this.handleSaveUnit()}
                                                    />
                                                </EditPanelCell>
                                            </EditPanelRow>
                                            {this.getUnitCustomInspectionStandardData()}
                                            <EditPanelRow>
                                                <RoleDependent
                                                    allowed={[Roles.Administrator, Roles.Client]}
                                                    isNotAuthorized={!this.state.surveyId}
                                                    content={
                                                        <EditPanelCell rowCount={6}>
                                                            <EditPanelSwitch
                                                                name="unitIsSurveySpecific"
                                                                placeholder={this.props.t('object.inspectionSpecific')}
                                                                handleChange={this.handleChange.bind(this)}
                                                                value={this.state.unitIsSurveySpecific}
                                                            />
                                                        </EditPanelCell>
                                                    }
                                                />
                                                <EditPanelCell rowCount={6}>
                                                    <EditPanelSwitch
                                                        name="unitIsArchived"
                                                        placeholder={this.props.t('common.isArchived')}
                                                        handleChange={this.handleChange.bind(this)}
                                                        value={this.state.unitIsArchived}
                                                    />
                                                </EditPanelCell>
                                            </EditPanelRow>

                                            <EditPanelButtons>
                                                <EditPanelButton
                                                    onClick={() => this.handleCloseFormClicked()}
                                                    small={true}
                                                    text={this.props.t('common.cancel')}
                                                />
                                                <EditPanelButton
                                                    onClick={async () => this.handleSaveUnit()}
                                                    small={true}
                                                    primary={true}
                                                    disabled={this.state.submitting}
                                                    text={this.props.t('common.saveItem', this.props.t('common.unit'))}
                                                />
                                            </EditPanelButtons>
                                        </div>
                                    </PanelWithoutForm>
                                </form>
                            </div>
                            {/* Add manifestation */}
                            <div id="popup_manifestation" className={classNames({ hidden: !this.state.showManifestationAddForm })}>
                                <form method="post" action="/" id="admin-form">
                                    <PanelWithoutForm>
                                        <PanelHeading text={this.props.t('common.addItem', this.props.t('common.manifestations'))} />
                                        <div className="panel-body">
                                            <EditPanelSection>
                                                <EditPanelInput
                                                    name="manifestationFormName"
                                                    placeholder={this.props.t('common.name')}
                                                    value={this.state.manifestationFormName}
                                                    icon="fa fa-pagelines"
                                                    handleChange={this.handleChange.bind(this)}
                                                    notValid={this.state.addManifestationNameRequiredError}
                                                />
                                            </EditPanelSection>
                                            <EditPanelRow>
                                                <label className="field-label col-md-2">Aantal:</label>
                                                <EditPanelCell rowCount={2}>
                                                    <label className="field select">
                                                        <select
                                                            value={this.state.manifestationFormTotal}
                                                            onChange={async (e) =>
                                                                this.setState({ manifestationFormTotal: parseInt(e.target.value, 10) })
                                                            }
                                                        >
                                                            {this.getIntegerArray(20).map((i) => (
                                                                <option key={i}>{i}</option>
                                                            ))}
                                                        </select>
                                                    </label>
                                                </EditPanelCell>
                                                <div className="col-md-4 hidden">
                                                    <EditPanelInput
                                                        name="manifestationFormCode"
                                                        placeholder={this.props.t('common.code')}
                                                        value={this.state.manifestationFormCode}
                                                        icon="fa fa-dashboard"
                                                        handleChange={this.handleChange.bind(this)}
                                                        notValid={this.state.addManifestationNameRequiredError}
                                                    />
                                                </div>
                                            </EditPanelRow>
                                            <RoleDependent
                                                allowed={[Roles.Administrator, Roles.Client]}
                                                isNotAuthorized={!this.state.surveyId}
                                                content={
                                                    <EditPanelSection>
                                                        <EditPanelSwitch
                                                            name="manifestationIsSurveySpecific"
                                                            placeholder={this.props.t('object.inspectionSpecific')}
                                                            handleChange={this.handleChange.bind(this)}
                                                            value={this.state.manifestationIsSurveySpecific}
                                                        />
                                                    </EditPanelSection>
                                                }
                                            />
                                            <EditPanelButtons>
                                                <EditPanelButton
                                                    onClick={() => this.handleCloseFormClicked()}
                                                    small={true}
                                                    text={this.props.t('common.cancel')}
                                                />
                                                <EditPanelButton
                                                    onClick={async () => this.addNewManifestations()}
                                                    small={true}
                                                    primary={true}
                                                    disabled={this.state.submitting}
                                                    text={this.props.t('common.saveItem', this.props.t('common.manifestation'))}
                                                />
                                            </EditPanelButtons>
                                        </div>
                                    </PanelWithoutForm>
                                </form>
                            </div>
                            {/* Edit manifestation */}
                            <div id="popup_manifestation" className={classNames({ hidden: !this.state.showManifestationEditForm })}>
                                <form method="post" action="/" id="admin-form">
                                    <PanelWithoutForm>
                                        <PanelHeading text={this.props.t('common.editItem', this.props.t('common.manifestation'))} />
                                        <div className="panel-body">
                                            <EditPanelSection>
                                                <EditPanelInput
                                                    name="manifestationFormName"
                                                    placeholder={this.props.t('common.name')}
                                                    value={this.state.manifestationFormName}
                                                    icon="fa fa-pagelines"
                                                    handleChange={this.handleChange.bind(this)}
                                                    handleEnter={async () => this.handleSaveManifestation()}
                                                    notValid={this.state.addManifestationNameRequiredError}
                                                />
                                            </EditPanelSection>
                                            <EditPanelRow>
                                                <EditPanelCell rowCount={8}>
                                                    <EditPanelInput
                                                        name="manifestationFormLocation"
                                                        placeholder={this.props.t('common.location')}
                                                        value={this.state.manifestationFormLocation}
                                                        icon="fa fa-map-marker"
                                                        handleChange={this.handleChange.bind(this)}
                                                        handleEnter={async () => this.handleSaveManifestation()}
                                                    />
                                                </EditPanelCell>
                                                <EditPanelCell rowCount={4}>
                                                    <EditPanelInput
                                                        name="manifestationFormCode"
                                                        placeholder={this.props.t('common.code')}
                                                        value={this.state.manifestationFormCode}
                                                        icon="fa fa-dashboardr"
                                                        handleChange={this.handleChange.bind(this)}
                                                        handleEnter={async () => this.handleSaveManifestation()}
                                                    />
                                                </EditPanelCell>
                                            </EditPanelRow>
                                            <RoleDependent
                                                allowed={[Roles.Administrator, Roles.Client]}
                                                isNotAuthorized={!this.state.surveyId}
                                                content={
                                                    <EditPanelSection>
                                                        <EditPanelSwitch
                                                            name="manifestationIsSurveySpecific"
                                                            placeholder={this.props.t('object.inspectionSpecific')}
                                                            handleChange={this.handleChange.bind(this)}
                                                            value={this.state.manifestationIsSurveySpecific}
                                                        />
                                                    </EditPanelSection>
                                                }
                                            />
                                            <EditPanelButtons>
                                                <EditPanelButton
                                                    onClick={() => this.handleCloseFormClicked()}
                                                    small={true}
                                                    text={this.props.t('common.cancel')}
                                                />
                                                <EditPanelButton
                                                    onClick={async () => this.handleSaveManifestation()}
                                                    small={true}
                                                    primary={true}
                                                    disabled={this.state.submitting}
                                                    text={this.props.t('common.saveItem', this.props.t('common.manifestation'))}
                                                />
                                            </EditPanelButtons>
                                        </div>
                                    </PanelWithoutForm>
                                </form>
                            </div>
                            {/* Add/edit element */}
                            <div id="popup_element" className={classNames({ hidden: !this.state.showElementForm })}>
                                <form method="post" action="/" id="admin-form" onSubmit={async () => this.handleSaveElement()}>
                                    <PanelWithoutForm>
                                        <PanelHeading text={this.props.t('common.element')} />
                                        <div className="panel-body">
                                            {this.state.objectTypeElements.length > 0 && !isEemuaOrApi(this.state.inspectionStandardType) && (
                                                <EditPanelSection>
                                                    <label className="field select">
                                                        <select
                                                            id="element"
                                                            name="element"
                                                            value={this.state.elementFormObjectTypeElementCode}
                                                            onChange={async (e) =>
                                                                this.handleChange(e.target.value, 'elementFormObjectTypeElementCode')
                                                            }
                                                        >
                                                            <option value="">
                                                                {this.props.t('common.selectItem', this.props.t('common.element'))}
                                                            </option>
                                                            {this.state.objectTypeElements.map((objectTypeElement, i) => (
                                                                <option key={i} value={objectTypeElement.code}>
                                                                    {objectTypeElement.name} ({objectTypeElement.code}){' '}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        <i className="arrow"></i>
                                                    </label>
                                                </EditPanelSection>
                                            )}
                                            <EditPanelSection>
                                                <EditPanelInput
                                                    name="elementFormName"
                                                    placeholder={this.props.t('object.customElement')}
                                                    value={this.state.elementFormName}
                                                    icon="fa fa-pagelines"
                                                    handleChange={this.handleChange.bind(this)}
                                                    handleEnter={async () => this.handleSaveElement()}
                                                    notValid={this.state.addElementNameRequiredError}
                                                />
                                            </EditPanelSection>

                                            <EditPanelRow>
                                                <EditPanelCell rowCount={2}>
                                                    <EditPanelInput
                                                        name="elementFormCode"
                                                        placeholder={this.props.t('common.code')}
                                                        value={this.state.elementFormCode}
                                                        icon="fa fa-dashboardr"
                                                        handleChange={this.handleChange.bind(this)}
                                                        handleEnter={async () => this.handleSaveElement()}
                                                    />
                                                </EditPanelCell>
                                                <EditPanelCell rowCount={4}>
                                                    <EditPanelInput
                                                        name="elementFormLocation"
                                                        placeholder={this.props.t('common.location')}
                                                        value={this.state.elementFormLocation}
                                                        icon="fa fa-map-marker"
                                                        handleChange={this.handleChange.bind(this)}
                                                        handleEnter={async () => this.handleSaveElement()}
                                                    />
                                                </EditPanelCell>
                                                <EditPanelCell rowCount={4}>
                                                    <EditPanelInput
                                                        name="elementSupplier"
                                                        placeholder={this.props.t('object.supplier')}
                                                        value={this.state.elementSupplier}
                                                        handleChange={this.handleChange.bind(this)}
                                                        handleEnter={async () => this.handleSaveElement()}
                                                    />
                                                </EditPanelCell>
                                                <EditPanelCell rowCount={2}>
                                                    <EditPanelInput
                                                        name="elementConstructionYear"
                                                        placeholder={this.props.t('object.constructionYear')}
                                                        value={this.state.elementConstructionYear}
                                                        restrictToNumbers={true}
                                                        handleChange={(value) =>
                                                            void this.handleChange(
                                                                value === '' ? null : parseInt(value, 10),
                                                                'elementConstructionYear',
                                                            )
                                                        }
                                                        handleEnter={async () => this.handleSaveElement()}
                                                    />
                                                </EditPanelCell>
                                            </EditPanelRow>
                                            <EditPanelRow>
                                                <EditPanelCell rowCount={12}>
                                                    <EditPanelInput
                                                        name="elementRemarks"
                                                        placeholder={this.props.t('common.remarks')}
                                                        value={this.state.elementRemarks}
                                                        handleChange={this.handleChange.bind(this)}
                                                        handleEnter={async () => this.handleSaveElement()}
                                                    />
                                                </EditPanelCell>
                                            </EditPanelRow>

                                            {this.state.elementIsStructural &&
                                                this.state.elementGroupName !== undefined &&
                                                isCustomerVersion('zxy') && (
                                                    <EditPanelSection>
                                                        <label className="field select">
                                                            <select
                                                                value={this.state.elementConstructionType ?? ''}
                                                                onChange={async (e) => this.handleChange(e.target.value, 'elementConstructionType')}
                                                            >
                                                                <option value="">{this.props.t('common.selectItem', 'Constructie type')}</option>
                                                                {(
                                                                    elementConstructionTypeList.find(
                                                                        (i) => i.value === this.state.elementGroupName,
                                                                    ) ?? {
                                                                        items: [],
                                                                    }
                                                                ).items.map((item) => (
                                                                    <option value={item.value} key={item.value}>
                                                                        {item.text}
                                                                    </option>
                                                                ))}
                                                            </select>{' '}
                                                            <i className="arrow"></i>
                                                        </label>
                                                    </EditPanelSection>
                                                )}
                                            {this.getElementCustomInspectionStandardData()}
                                            <EditPanelRow>
                                                <RoleDependent
                                                    allowed={[Roles.Administrator, Roles.Client]}
                                                    isNotAuthorized={!this.state.surveyId}
                                                    content={
                                                        <EditPanelCell rowCount={6}>
                                                            <EditPanelSwitch
                                                                name="elementIsSurveySpecific"
                                                                placeholder={this.props.t('object.inspectionSpecific')}
                                                                handleChange={this.handleChange.bind(this)}
                                                                value={this.state.elementIsSurveySpecific}
                                                            />
                                                        </EditPanelCell>
                                                    }
                                                />
                                                <EditPanelCell rowCount={6}>
                                                    <EditPanelSwitch
                                                        name="elementIsArchived"
                                                        placeholder={this.props.t('common.isArchived')}
                                                        handleChange={this.handleChange.bind(this)}
                                                        value={this.state.elementIsArchived}
                                                    />
                                                </EditPanelCell>
                                            </EditPanelRow>
                                            <EditPanelButtons>
                                                <EditPanelButton
                                                    onClick={() => this.handleCloseFormClicked()}
                                                    small={true}
                                                    text={this.props.t('common.cancel')}
                                                />
                                                <EditPanelButton
                                                    onClick={async () => this.handleSaveElement()}
                                                    small={true}
                                                    primary={true}
                                                    disabled={this.state.submitting}
                                                    text={this.props.t('common.saveItem', this.props.t('common.element'))}
                                                />
                                            </EditPanelButtons>
                                        </div>
                                    </PanelWithoutForm>
                                </form>
                            </div>{' '}
                            {/* End popup_element */}
                            <div
                                id="background_popup"
                                className={classNames({
                                    hidden:
                                        !this.state.showElementForm &&
                                        !this.state.showUnitForm &&
                                        !this.state.showManifestationEditForm &&
                                        !this.state.showManifestationAddForm,
                                })}
                                style={{ opacity: 0.6, display: 'block' }}
                                onClick={() => this.handleCloseFormClicked()}
                            ></div>
                        </Panel>
                    </Tray>
                </Content>
            </div>
        );
    }

    private getElementCustomInspectionStandardData() {
        const inspectionStandardTypes = getInspectionStandardTypes();
        const result: any[] = [];
        for (const inspectionStandardType of inspectionStandardTypes) {
            if (customInspectionTypeFrontEndSettings[inspectionStandardType]?.getDecompositionElementAddEdit) {
                // eslint-disable-next-line @typescript-eslint/unbound-method
                const DecompositionElementAddEdit = customInspectionTypeFrontEndSettings[inspectionStandardType]?.getDecompositionElementAddEdit;
                if (DecompositionElementAddEdit) {
                    result.push(
                        <DecompositionElementAddEdit
                            key={inspectionStandardType}
                            handleChange={this.handleChange.bind(this)}
                            inspectionStandardData={this.state.elementInspectionStandardData}
                        />,
                    );
                }
            }
        }
        return result;
    }

    private getUnitCustomInspectionStandardData() {
        const inspectionStandardTypes = getInspectionStandardTypes();
        const result: any[] = [];
        for (const inspectionStandardType of inspectionStandardTypes) {
            if (customInspectionTypeFrontEndSettings[inspectionStandardType]?.getDecompositionUnitAddEdit) {
                // eslint-disable-next-line @typescript-eslint/unbound-method
                const DecompositionUnitAddEdit = customInspectionTypeFrontEndSettings[inspectionStandardType]?.getDecompositionUnitAddEdit;
                if (DecompositionUnitAddEdit) {
                    result.push(
                        <DecompositionUnitAddEdit
                            key={inspectionStandardType}
                            handleChange={this.handleChange.bind(this)}
                            inspectionStandardData={this.state.unitInspectionStandardData}
                        />,
                    );
                }
            }
        }
        return result;
    }

    private useManifestations() {
        if (
            this.state.inspectionStandardType === InspectionStandardTypes.Nen2767 ||
            this.state.inspectionStandardType === InspectionStandardTypes.Free ||
            this.state.inspectionStandardType === InspectionStandardTypes.Fmeca
        ) {
            return true;
        }
        return false;
    }

    private getIntegerArray(to: number) {
        const result: number[] = [];
        for (let i = 1; i <= to; i++) {
            result.push(i);
        }
        return result;
    }

    private async handleChange(value: any, stateKey: string) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const newState = { [stateKey]: value } as any;

        if (stateKey.startsWith('elementInspectionStandardData')) {
            const valueInObject: any = flat.unflatten({ [stateKey]: value });
            const newData = mergeObjects(this.state.elementInspectionStandardData, valueInObject.elementInspectionStandardData);
            newState.elementInspectionStandardData = newData;
        } else if (stateKey.startsWith('unitInspectionStandardData')) {
            const valueInObject: any = flat.unflatten({ [stateKey]: value });
            const newData = mergeObjects(this.state.unitInspectionStandardData, valueInObject.unitInspectionStandardData);
            newState.unitInspectionStandardData = newData;
        } else if (stateKey === 'elementFormObjectTypeElementCode') {
            const objectTypeElement = this.state.objectTypeElements.find((i) => i.code === value);
            if (objectTypeElement) {
                newState.elementFormName = objectTypeElement.name;
                newState.elementFormCode = objectTypeElement.code;
            }
        } else if (stateKey === 'unitFormObjectTypeUnitCode') {
            const objectTypeUnit = this.state.objectTypeUnits.find((i) => i.code === value);
            if (objectTypeUnit) {
                newState.unitFormName = objectTypeUnit.name;
                newState.unitFormCode = objectTypeUnit.code;
            }
        }

        await this.setState(newState);
    }

    private async handleSaveElement() {
        const element = {
            location: this.state.elementFormLocation,
            name: this.state.elementFormName,
            code: this.state.elementFormCode,
            constructionType: this.state.elementConstructionType,
            constructionYear: this.state.elementConstructionYear,
            isStructural: this.state.elementIsStructural,
            elementGroupName: this.state.elementGroupName,
            isArchived: this.state.elementIsArchived,
            surveyId: this.state.surveyId,
            isSurveySpecific: this.state.elementIsSurveySpecific,
            inspectionStandardData: this.state.elementInspectionStandardData,
            categoryId: this.state.elementCategoryId,
            supplier: this.state.elementSupplier,
            remarks: this.state.elementRemarks,
        } as any;

        if (!element.name || element.name === '') {
            await this.setState({ addElementNameRequiredError: true });
        } else {
            await this.setState({ addElementNameRequiredError: false });
            await this.setState({ message: undefined, submitting: true, errorCode: undefined, failed: false });

            let url = `/api/objects/${this.props.match.params.id}/elements`;
            if (this.state.elementFormId) {
                url += `/${this.state.elementFormId}`;
            }

            try {
                await fetchJsonWithThrow(url, 'POST', element);
                await this.load();
                await this.setState({ showElementForm: false, showUnitForm: false });
            } catch (error) {
                await this.setState({ submitting: false, errorCode: error.message, failed: true });
            }
        }
    }

    private handleAddElementClicked(categoryId?: string) {
        void this.setState({
            showElementForm: true,
            elementFormObjectTypeElementCode: '',
            elementFormCode: undefined,
            elementFormLocation: undefined,
            elementFormName: undefined,
            elementFormId: undefined,
            elementConstructionType: undefined,
            elementConstructionYear: null,
            elementIsStructural: false,
            elementGroupName: undefined,
            elementIsArchived: false,
            elementIsSurveySpecific: false,
            elementInspectionStandardData: {},
            elementCategoryId: categoryId,
            elementSupplier: '',
            elementRemarks: '',
        });
    }

    private handleEditElementClicked(elementId: string) {
        for (let i = 0; i < this.state.elements.length; i++) {
            const element = this.state.elements[i]!;

            if (element.id === elementId) {
                void this.setState({
                    showElementForm: true,
                    elementFormObjectTypeElementCode: '',
                    elementFormCode: element.code,
                    elementFormLocation: element.location,
                    elementFormName: element.name,
                    elementFormId: element.id,
                    elementConstructionType: element.constructionType ?? undefined,
                    elementConstructionYear: element.constructionYear ?? null,
                    elementIsStructural: element.isStructural,
                    elementGroupName: element.elementGroupName ?? undefined,
                    elementIsArchived: element.isArchived,
                    elementIsSurveySpecific: element.surveyId !== null,
                    elementInspectionStandardData: element.inspectionStandardData,
                    elementSupplier: element.supplier,
                    elementRemarks: element.remarks,
                });

                break;
            }
        }
    }

    private async handleSaveUnit() {
        const unit = {
            location: this.state.unitFormLocation,
            elementId: this.state.unitFormElementId,
            name: this.state.unitFormName,
            code: this.state.unitFormCode,
            material: this.state.unitFormMaterial,
            quantity: Math.ceil(this.state.unitFormQuantity ?? 0),
            quantityUnitOfMeasurement: this.state.unitFormQuantityUnitOfMeasurement,
            constructionYear: this.state.unitConstructionYear,
            isArchived: this.state.unitIsArchived,
            surveyId: this.state.surveyId,
            isSurveySpecific: this.state.unitIsSurveySpecific,
            inspectionStandardData: this.state.unitInspectionStandardData,
            dimension: this.state.unitDimension,
            strength: this.state.unitStrength,
            norm: this.state.unitNorm,
            supplier: this.state.unitSupplier,
            remarks: this.state.unitRemarks,
        };

        if (!unit.name || unit.name === '') {
            await this.setState({ addUnitNameRequiredError: true });
        } else {
            await this.setState({ addUnitNameRequiredError: false, message: undefined, submitting: true, errorCode: undefined, failed: false });

            let url = `/api/objects/${this.props.match.params.id}/units`;
            if (this.state.unitFormId) {
                url += `/${this.state.unitFormId}`;
            }
            try {
                await fetchJsonWithThrow(url, 'POST', unit);
                await this.load();
                await this.setState({ showUnitForm: false, compositionChanged: true });
            } catch (error) {
                await this.setState({ submitting: false, errorCode: error.message, failed: true });
            }
        }
    }

    private async handleSaveManifestation() {
        const manifestation = {
            location: this.state.manifestationFormLocation,
            elementId: this.state.manifestationFormElementId,
            name: this.state.manifestationFormName,
            code: this.state.manifestationFormCode,
            surveyId: this.state.surveyId,
            isSurveySpecific: this.state.manifestationIsSurveySpecific,
            inspectionStandardData: this.state.unitInspectionStandardData,
        };

        if (!manifestation.name || manifestation.name === '') {
            await this.setState({ addManifestationNameRequiredError: true });
        } else {
            await this.setState({
                addManifestationNameRequiredError: false,
                message: undefined,
                submitting: true,
                errorCode: undefined,
                failed: false,
            });

            let url = `/api/objects/${this.props.match.params.id}/manifestations`;
            if (this.state.manifestationFormId) {
                url += `/${this.state.manifestationFormId}`;
            }
            try {
                await fetchJsonWithThrow(url, 'POST', manifestation);
                await this.load();
                await this.setState({ showManifestationEditForm: false });
            } catch (error) {
                await this.setState({ submitting: false, errorCode: error.message, failed: true });
            }
        }
    }

    private handleAddUnitClicked(elementId: string, elementCode: string) {
        void this.setState({ objectTypeUnits: [] });
        if (this.state.objectTypeElements.length > 0) {
            for (let i = 0; i < this.state.objectTypeElements.length; i++) {
                const element = this.state.objectTypeElements[i]!;
                if (element.code === elementCode) {
                    void this.setState({ objectTypeUnits: element.units });
                    break;
                }
            }
        }

        void this.setState({
            showUnitForm: true,
            unitFormObjectTypeUnitCode: '',
            unitFormCode: undefined,
            unitFormLocation: undefined,
            unitFormName: undefined,
            unitFormId: undefined,
            unitFormElementId: elementId,
            unitConstructionYear: null,
            unitIsArchived: false,
            unitIsSurveySpecific: false,
            unitInspectionStandardData: {},
            unitDimension: '',
            unitStrength: '',
            unitNorm: '',
            unitSupplier: '',
            unitRemarks: '',
        });
    }

    private handleAddManifestations(unit: IUnit) {
        void this.setState({
            showManifestationAddForm: true,
            manifestationFormCode: unit.code || '',
            manifestationFormName: unit.name || '',
            manifestationFormUnitId: unit.id,
            manifestationIsSurveySpecific: false,
            manifestationFormTotal: 1,
        });
    }

    private async addNewManifestations() {
        await this.setState({ submitting: true, errorCode: undefined, failed: false });
        const url = `/api/objects/${this.props.match.params.id}/units/${this.state.manifestationFormUnitId}/manifestations/bulk`;
        await fetchJsonWithThrow(url, 'POST', {
            total: this.state.manifestationFormTotal,
            name: this.state.manifestationFormName,
            code: this.state.manifestationFormCode,
            surveyId: this.state.surveyId,
            isSurveySpecific: this.state.manifestationIsSurveySpecific,
        });
        await this.load();
        await this.setState({ submitting: false, showManifestationAddForm: false });
    }

    private getElementFromState(elementId: string) {
        for (let i = 0; i < this.state.elements.length; i++) {
            const element = this.state.elements[i]!;
            if (element.id === elementId) {
                return element;
            }
        }
        return undefined;
    }

    private getUnitFromState(elementId: string, unitId: string) {
        const element = this.getElementFromState(elementId);

        if (!element) {
            return undefined;
        }

        for (let i = 0; i < element.units.length; i++) {
            const unit = element.units[i]!;
            if (unit.id === unitId) {
                return unit;
            }
        }
        return undefined;
    }

    private handleEditUnitClicked(elementId: string, unitId: string) {
        const element = this.getElementFromState(elementId);
        const unit = this.getUnitFromState(elementId, unitId);
        if (!unit) {
            return;
        }

        void this.setState({ objectTypeUnits: [] });
        if (this.state.objectTypeElements.length > 0) {
            for (let i = 0; i < this.state.objectTypeElements.length; i++) {
                const objectTypElement = this.state.objectTypeElements[i]!;
                if (element && objectTypElement.code === element.code) {
                    void this.setState({ objectTypeUnits: objectTypElement.units });
                    break;
                }
            }
        }

        void this.setState({
            showUnitForm: true,
            unitFormObjectTypeUnitCode: '',
            unitFormCode: unit.code,
            unitFormLocation: unit.location,
            unitFormMaterial: unit.material,
            unitFormQuantity: unit.quantity,
            unitFormQuantityUnitOfMeasurement: unit.quantityUnitOfMeasurement || 'm',
            unitFormName: unit.name,
            unitFormId: unit.id,
            unitConstructionYear: unit.constructionYear ?? null,
            unitIsArchived: unit.isArchived,
            unitIsSurveySpecific: unit.surveyId !== null,
            unitInspectionStandardData: unit.inspectionStandardData,
            unitDimension: unit.dimension,
            unitStrength: unit.strength,
            unitNorm: unit.norm,
            unitSupplier: unit.supplier,
            unitRemarks: unit.remarks,
        });
    }

    private handleEditManifestationClicked(manifestation: IManifestation) {
        void this.setState({
            showManifestationEditForm: true,
            manifestationFormCode: manifestation.code,
            manifestationFormLocation: manifestation.location,
            manifestationFormName: manifestation.name,
            manifestationFormId: manifestation.id,
            manifestationIsSurveySpecific: manifestation.surveyId !== null,
        });
    }

    private handleCloseFormClicked() {
        void this.setState({ showElementForm: false, showUnitForm: false, showManifestationEditForm: false, showManifestationAddForm: false });
    }

    private async load() {
        await this.setState({ submitting: true });
        let url = `/api/objects/${this.props.match.params.id}/elements?includeAssetConditionCount=true`;
        if (this.props.location.query.surveyid) {
            url = `/api/objects/${this.props.match.params.id}/elements?surveyId=${this.props.location.query.surveyid}&includeAssetConditionCount=true`;
        }
        const result = await fetchGet(url);
        await this.setState({ submitting: false, elements: result.json });
    }

    private async loadCategories() {
        await this.setState({ submitting: true });
        const url = `/api/objects/${this.props.match.params.id}/categories`;
        const result = await fetchGet(url);
        await this.setState({ submitting: false, categories: result.json });
    }

    private async loadObjectTypeElements(inspectionStandardId: string) {
        await this.setState({ submitting: true });
        const url = `/api/inspectionStandards/${inspectionStandardId}/elements`;
        const result = await fetchGet(url);
        await this.setState({ submitting: false, objectTypeElements: result.json });
    }

    private async loadObject() {
        await this.setState({ submitting: true });

        const url = `/api/objects/${this.props.match.params.id}`;
        const result = await fetchGet(url);
        if (
            this.props.location.query.inspectionStandardId &&
            this.props.location.query.inspectionStandardId !== 'undefined' &&
            this.props.location.query.inspectionStandardId !== 'null'
        ) {
            await this.loadObjectTypeElements(this.props.location.query.inspectionStandardId);
        } else if (result.json.inspectionStandardId) {
            await this.loadObjectTypeElements(result.json.inspectionStandardId);
        }
        await this.setState({
            submitting: false,
            name: result.json.name,
            inspectionStandardId: result.json.inspectionStandardId,
            inspectionStandardType: result.json.inspectionStandardType,
            canEditObject: result.json.canEdit,
        });
    }

    private removeElement(element: IElement) {
        if (element.assetsCount + element.conditionsCount === 0) {
            void this.remove(`/api/objects/${this.props.match.params.id}/elements/${element.id}`);
        } else {
            toast.error(this.props.t('object.itemCannotBeRemoved'), { autoClose: 3000 });
        }
    }

    private hasUnitManifestationsWithAssetsOrConditions(unit: IUnit) {
        if (unit.manifestations.find((manifestation) => manifestation.assetsCount > 0 || manifestation.conditionsCount > 0)) {
            return true;
        }
        return false;
    }

    private async removeUnit(unit: IUnit) {
        if (unit.assetsCount + unit.conditionsCount === 0 && !this.hasUnitManifestationsWithAssetsOrConditions(unit)) {
            await this.remove(`/api/objects/${this.props.match.params.id}/units/${unit.id}`);
        } else {
            toast.error(this.props.t('object.itemCannotBeRemoved'), { autoClose: 3000 });
        }
    }

    private removeManifestation(manifestation: IManifestation) {
        if (manifestation.assetsCount + manifestation.conditionsCount === 0) {
            void this.remove(`/api/objects/${this.props.match.params.id}/manifestations/${manifestation.id}`);
        } else {
            toast.error(this.props.t('object.itemCannotBeRemoved'), { autoClose: 3000 });
        }
    }

    private async remove(url: string) {
        await this.setState({ submitting: true });
        const result = await fetchDelete(url, undefined, undefined, [500]);
        if (result.status === 500) {
            toast.error(this.props.t('object.itemCannotBeRemoved'), { autoClose: 3000 });
        }
        await this.load();
    }

    private async resetDefaultMaintenance() {
        const url = `/api/surveys/${this.props.location.query.surveyid}/resetDefaultMaintenance?objectId=${this.props.match.params.id}`;
        if (!this.state.submitting) {
            await this.setState({ submitting: true });
            await fetchPost(url, {});
            await this.load();
            await this.setState({ saved: true, submitting: false, compositionChanged: false });
        }
    }
}

// eslint-disable-next-line import/no-default-export
export default translate()(connect(mapStateToProps, undefined, undefined, { forwardRef: true })(ObjectCompositionEdit));
